"use client"
import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import api, { restoreAuthFromStorage } from "../api/Client";

// 🔹 importa tus iconos (ajusta las rutas según dónde los guardes)
import iconCalendar from "../assets/icons/calendar.png";
import iconUser from "../assets/icons/user.png";
import iconCommunity from "../assets/icons/community.png";
import iconGlobal from "../assets/icons/global.png";
import iconShop from "../assets/icons/shop.png";
import logoSinFondo from "../assets/logo sin fondo.png";
import beMoreHumanNoBg from "../assets/be-more-human-no-bg.png";

type HumanScoreBreakdown = {
  attendance?: { weeksAttended?: number; windowWeeks?: number; score?: number };
  frequency?: { trainingsLast12Weeks?: number; avgPerWeek?: number; score?: number };
  extraServices?: { usedInLast8Weeks?: boolean; count?: number; score?: number };
};

type HumanScoreResponse = { humanScore?: number; score?: number; breakdown?: HumanScoreBreakdown };

// Tipos para los rangos de Anovator (basado en API docs)
type AnovatorStandardRange = {
  fewest?: number;   // Muy bajo (Límite inferior extremo)
  fewer?: number;    // Bajo (Límite inferior normal)
  more?: number;     // Alto (Límite superior normal)
  most?: number;     // Muy alto (Límite superior extremo)
  standard?: number; // Valor ideal
};

type AnovatorStandards = {
  weightStandard?: AnovatorStandardRange;
  bmiStandard?: AnovatorStandardRange;
  fatStandard?: AnovatorStandardRange;
  muscleStandard?: AnovatorStandardRange;
  waterStandard?: AnovatorStandardRange;
  visfatStandard?: AnovatorStandardRange;
  subfatStandard?: AnovatorStandardRange;
  bmrStandard?: AnovatorStandardRange;
  proteinStandard?: AnovatorStandardRange;
  boneStandard?: AnovatorStandardRange;
};

type AnovatorExam = {
  id: string;
  gmtCreate: number;
  weight?: number;
  bmi?: number;
  fat?: number;
  muscle?: number;
  water?: number;
  inFat?: number;
  subFat?: number;
  bmr?: number;
  bone?: number;
  protein?: number;
  bodyAge?: number;
  score?: number;
  standard?: AnovatorStandards; // Objeto con los rangos dinámicos
};

type RangeStatus = "very_low" | "low" | "normal" | "high" | "very_high" | "neutral";

const RANGE_STYLES: Record<RangeStatus, { bg: string; border: string; text: string; label: string }> = {
  // 1. Muy Bajo (< fewest): Violeta / Índigo
  very_low: {
    bg: "#F3E5F5",        // Violeta muy claro
    border: "#CE93D8",    // Violeta medio
    text: "#7B1FA2",      // Violeta oscuro
    label: "Muy Bajo"
  },
  // 2. Bajo (fewest - fewer): Azul Claro
  low: {
    bg: "#E1F5FE",        // Azul muy claro
    border: "#81D4FA",    // Azul cielo
    text: "#0277BD",      // Azul oscuro
    label: "Bajo"
  },
  // 3. Normal (fewer - more): Verde (Zona ideal)
  normal: {
    bg: "#E8F5E9",        // Verde muy claro
    border: "#A5D6A7",    // Verde suave
    text: "#2E7D32",      // Verde bosque
    label: "Normal"
  },
  // 4. Alto (more - most): Naranja (Alerta)
  high: {
    bg: "#FFF3E0",        // Naranja muy claro
    border: "#FFCC80",    // Naranja suave
    text: "#EF6C00",      // Naranja fuerte
    label: "Alto"
  },
  // 5. Muy Alto (> most): Rojo / Rosa (Excesivo)
  very_high: {
    bg: "#FFEBEE",        // Rojo muy claro
    border: "#EF9A9A",    // Rojo suave
    text: "#C62828",      // Rojo fuerte
    label: "Muy Alto"
  },
  // Sin datos
  neutral: {
    bg: "#FAFAFA",
    border: "#F5F5F5",
    text: "#616161",
    label: "—"
  },
};

export default function StatisticsPage() {
  const [humanScore, setHumanScore] = useState<number | null>(null);
  const [loadingScore, setLoadingScore] = useState<boolean>(true);
  const [errorScore, setErrorScore] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<HumanScoreBreakdown | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareInfo, setShareInfo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"resumen" | "anovator">("resumen");

  const [anovatorExams, setAnovatorExams] = useState<AnovatorExam[] | null>(null);
  const [loadingAnovator, setLoadingAnovator] = useState(false);
  const [errorAnovator, setErrorAnovator] = useState<string | null>(null);

  useEffect(() => {
    async function fetchScore() {
      try {
        setLoadingScore(true);
        setErrorScore(null);
        const { user } = await restoreAuthFromStorage();
        if (!user?.id) throw new Error("Usuario no disponible");
        const res = await api.get<HumanScoreResponse>(`/api/customers/human-score/${user.id}`);
        const score = Number(res.data?.humanScore ?? res.data?.score);
        if (!Number.isFinite(score)) throw new Error("Score inválido");
        setHumanScore(Number(score.toFixed(1)));
        setBreakdown(res.data?.breakdown ?? null);
      } catch (err: any) {
        console.warn("No se pudo cargar el Human Score:", err);
        setErrorScore("No se pudo cargar el Human Score");
        setHumanScore(null);
        setBreakdown(null);
      } finally {
        setLoadingScore(false);
      }
    }
    fetchScore();
  }, []);

  useEffect(() => {
    async function fetchAnovator() {
      try {
        setLoadingAnovator(true);
        setErrorAnovator(null);
        const { user } = await restoreAuthFromStorage();
        const phone = (user as any)?.phone;
        if (!phone) {
          setAnovatorExams([]);
          setErrorAnovator("Falta teléfono en perfil");
          return;
        }
        // Llamada al backend actualizado que devuelve data con 'standard'
        const res = await api.get<{ data: AnovatorExam[] }>("/api/anovator/exams", {
          params: { phone },
        });
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        list.sort((a, b) => (b.gmtCreate ?? 0) - (a.gmtCreate ?? 0));
        setAnovatorExams(list);
      } catch (e) {
        console.warn("Error Anovator:", e);
        setErrorAnovator("Error cargando mediciones");
        setAnovatorExams([]);
      } finally {
        setLoadingAnovator(false);
      }
    }
    if (activeTab === "anovator" && anovatorExams === null && !loadingAnovator) {
      fetchAnovator();
    }
  }, [activeTab, anovatorExams, loadingAnovator]);

  const scoreColor = getScoreColor(humanScore);
  const circleStyle: React.CSSProperties = {
    width: 140,
    height: 140,
    borderRadius: "50%",
    margin: "0 auto 12px",
    display: "grid",
    placeItems: "center",
    background: `conic-gradient(${scoreColor} ${humanScore ?? 0}%, #f0f0f0 ${humanScore ?? 0}%)`,
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
  };
  const innerCircleStyle: React.CSSProperties = {
    width: "68%",
    height: "68%",
    borderRadius: "50%",
    backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${logoSinFondo})`,
    backgroundSize: "110%",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: "#fff",
    border: "2px solid #fff",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    fontSize: humanScore !== null ? "22px" : "16px",
    color: scoreColor,
    textShadow: "0 1px 3px rgba(0,0,0,0.25)",
  };
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const tabButtonBase: React.CSSProperties = {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    borderRadius: "999px",
    cursor: "pointer",
  };
  const tabButtonActive: React.CSSProperties = {
    background: "#111",
    color: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  };
  const handleShare = async () => {
    if (typeof window === "undefined") return;

    const shareMessage =
      humanScore !== null
        ? `Mi Human Score es de: ${humanScore.toFixed(1)} en HUMAN.`
        : "Mi Human Score todavía no está disponible en HUMAN.";

    setSharing(true);
    setShareInfo(null);

    try {
      const pngBlob = await buildShareCard({
        score: humanScore,
        label: getScoreLabel(humanScore),
        breakdown,
      });

      let shared = false;

      // 1) Nativo (Capacitor) -> guardamos en caché y compartimos con Share
      const isNative = typeof Capacitor !== "undefined" && Capacitor.isNativePlatform?.();
      if (isNative) {
        try {
          const nativeUri = await persistImageForNativeShare(pngBlob);
          await Share.share({
            title: "Mi Human Score en HUMAN",
            text: shareMessage,
            url: nativeUri,
            dialogTitle: "Compartir Human Score",
          });
          shared = true;
        } catch (err) {
          console.warn("Native share falló, probamos web share", err);
        }
      }

      // 2) Web Share API con archivo
      if (!shared) {
        const file = new File([pngBlob], "human-score.png", { type: "image/png" });
        const nav: any = navigator;

        if (typeof nav?.canShare === "function" && nav.canShare({ files: [file] })) {
          shared = await nav
            .share({
              files: [file],
              title: "Mi Human Score en HUMAN",
              text: shareMessage,
              url: shareUrl || undefined,
            })
            .then(() => true)
            .catch(() => false);
        }

        // 3) Web Share API sin archivo
        if (!shared && typeof nav?.share === "function") {
          shared = await nav
            .share({
              title: "Mi Human Score en HUMAN",
              text: shareMessage,
              url: shareUrl || undefined,
            })
            .then(() => true)
            .catch(() => false);
        }
      }

      if (!shared) {
        triggerImageDownload(pngBlob, "human-score.png");
        setShareInfo(
          "Descargamos la imagen con tu Human Score. Compártela desde tu galería en la red que prefieras."
        );
      }
    } catch (err) {
      console.error("Error al preparar el share del Human Score", err);
      setShareInfo("No se pudo generar la imagen. Inténtalo de nuevo.");
    } finally {
      setSharing(false);
    }
  };

  const attendanceColor = getAttendanceColor(
    breakdown?.attendance?.weeksAttended,
    breakdown?.attendance?.windowWeeks
  );
  const frequencyColor = getFrequencyColor(breakdown?.frequency?.avgPerWeek);
  const extraServicesColor = getExtraServicesColor(breakdown?.extraServices?.count);

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        color: "#000",
        padding: "18px",
        paddingBottom: "120px",
        textAlign: "center",
        maxWidth: 520,
        margin: "0 auto",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      {/* === TÍTULO === */}
      <h1
        style={{
          fontSize: "20px",
          fontWeight: "800",
          marginBottom: "6px",
          textTransform: "uppercase",
        }}
      >
        LO QUE VIENE CON HUMAN
      </h1>
      <div
        style={{
          display: "flex",
          gap: "6px",
          background: "#f1f1f1",
          padding: "4px",
          borderRadius: "999px",
          margin: "4px 0 14px",
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("resumen")}
          style={{ ...tabButtonBase, ...(activeTab === "resumen" ? tabButtonActive : {}) }}
        >
          Resumen
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("anovator")}
          style={{ ...tabButtonBase, ...(activeTab === "anovator" ? tabButtonActive : {}) }}
        >
          Anovator
        </button>
      </div>
      {activeTab === "resumen" && (
        <>
          <p style={{ fontSize: "13px", margin: "0 0 4px" }}>
            Estamos desarrollando una nueva versión que elevará tu experiencia de
            entrenamiento, reserva y comunidad.
          </p>
          <p style={{ fontSize: "13px", fontWeight: "600", marginBottom: "12px" }}>
            Lo que hoy ves, mañana será historia.
          </p>

          {/* === HUMAN SCORE === */}
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: "12px",
              padding: "12px",
              marginBottom: "14px",
              background: "#fff",
              boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
            }}
          >
            <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: "13px" }}>
              Tu Human Score
            </p>
            <div style={circleStyle}>
              <div style={innerCircleStyle}>
                {loadingScore ? "..." : humanScore !== null ? humanScore : "—"}
              </div>
            </div>
            <div style={{ fontSize: "12px", marginTop: "6px" }}>
              {errorScore ? (
                <span style={{ color: "#c62828", fontWeight: 600 }}>{errorScore}</span>
              ) : (
                <>
                  <span style={{ fontWeight: 700, color: scoreColor }}>
                    {getScoreLabel(humanScore)}
                  </span>{" "}
                  · sobre 100
                </>
              )}
            </div>
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                fontSize: "11px",
              }}
            >
              <LegendDot color="#2e8b57" label="85-100" />
              <LegendDot color="#e8a317" label="70-85" />
              <LegendDot color="#c62828" label="50-70" />
              <LegendDot color="#888" label="<50" />
            </div>
            <div
              style={{
                marginTop: "12px",
                display: "grid",
                gap: "8px",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                textAlign: "left",
                fontSize: "11.5px",
              }}
            >
              <BreakdownCard
                title="Asistencia"
                value={`Has venido a entrenar ${formatNumberValue(breakdown?.attendance?.weeksAttended, 0)} semanas de las últimas ${breakdown?.attendance?.windowWeeks ?? 52
                  } semanas.`}
                score={breakdown?.attendance?.score ? Number(breakdown.attendance.score) : undefined}
                badgeColor={attendanceColor}
              />
              <BreakdownCard
                title="Frecuencia"
                value={`Tu frecuencia de entrenamiento ha sido de ${formatNumberValue(
                  breakdown?.frequency?.avgPerWeek,
                  1
                )} entrenos por semana.`}
                score={breakdown?.frequency?.score}
                badgeColor={frequencyColor}
              />
              <BreakdownCard
                title="Servicios extra"
                value={`Has contratado ${formatNumberValue(
                  breakdown?.extraServices?.count,
                  0
                )} sesiones de Fisioterapia o Nutrición.`}
                score={breakdown?.extraServices?.score}
                badgeColor={extraServicesColor}
              />
            </div>
            <div
              style={{
                marginTop: "14px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                onClick={handleShare}
                disabled={sharing}
                style={{
                  background: "linear-gradient(135deg, #b71c1c, #d32f2f)",
                  color: "#fff",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 700,
                  cursor: "pointer",
                  minWidth: 220,
                  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                  opacity: sharing ? 0.7 : 1,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!sharing) {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 14px rgba(0,0,0,0.16)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.12)";
                }}
              >
                {sharing ? "Preparando..." : "Compartir Human Score"}
              </button>
            </div>
            {shareInfo && (
              <p
                style={{
                  marginTop: "8px",
                  fontSize: "11px",
                  color: "#444",
                  textAlign: "center",
                }}
              >
                {shareInfo}
              </p>
            )}
          </div>

          {/* === BLOQUES FUNCIONALIDADES === */}
          <h2
            style={{
              fontSize: "14px",
              fontWeight: "800",
              marginBottom: "10px",
              letterSpacing: "0.5px",
            }}
          >
            PRÓXIMAS FUNCIONALIDADES
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
              fontSize: "12px",
            }}
          >
            <Feature
              icon={iconCalendar}
              title="Reservas inteligentes"
              text="Posibilidad de elegir centro, entrenador o fisio con hasta dos meses de antelación."
            />
            <Feature
              icon={iconUser}
              title="Perfil avanzado de usuario"
              text="Tu evolución, métricas y logros visibles conectados a tu historial de entrenamientos."
            />
            <Feature
              icon={iconCommunity}
              title="Comunidad HUMAN"
              text="Conecta con socios, comparte progresos y participa en eventos y desafíos exclusivos."
            />
            <Feature
              icon={iconGlobal}
              title="Acceso global HUMAN"
              text="Un único perfil para entrenar en todos los centros HUMAN."
            />
            <Feature
              icon={iconShop}
              title="Marketplace interno"
              text="Compra, canjea o regala experiencias HUMAN: entrenamientos, eventos y más."
            />
          </div>

          {/* === EN DESARROLLO (TIMELINE) === */}
          <h2
            style={{
              fontSize: "14px",
              fontWeight: "800",
              marginTop: "16px",
              marginBottom: "4px",
            }}
          >
            EN DESARROLLO
          </h2>

          <div style={{ fontSize: "12px", lineHeight: 1.2 }}>
            <div>
              <strong>Versión actual:</strong> HUMAN Perform App 1.0
            </div>
            <div
              style={{
                width: "65%",
                height: "3px",
                background: "#ccc",
                margin: "6px auto",
                position: "relative",
                borderRadius: "3px",
              }}
            >
              <div
                style={{
                  width: "50%",
                  height: "100%",
                  background: "#B71C1C",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  borderRadius: "3px",
                }}
              ></div>
            </div>
            <div>
              <strong>En desarrollo:</strong> HUMAN 2.0 – Integración total entre centros
            </div>
            <div style={{ marginTop: "4px" }}>
              <strong>Próximamente:</strong> HUMAN 3.0 – Comunidad, retos y conexión total
            </div>
          </div>

          {/* === CIERRE EMOCIONAL === */}
          <div style={{ marginTop: "18px", fontSize: "13px" }}>
            <p style={{ marginBottom: "4px" }}>Estamos evolucionando.</p>
            <p style={{ marginBottom: "8px" }}>
              No solo creamos entrenamientos, creamos el futuro del rendimiento humano.
            </p>
            <h2 style={{ color: "#B71C1C", fontWeight: "900", fontSize: "15px" }}>
              The Humans are coming.
            </h2>
          </div>
        </>
      )}
      {activeTab === "anovator" && (
        <div style={{ textAlign: "left" }}>
          <h2
            style={{
              fontSize: "14px",
              fontWeight: 800,
              margin: "6px 0 10px",
              letterSpacing: "0.4px",
              textAlign: "center",
            }}
          >
            MEDICIONES ANOVATOR A5
          </h2>
          {loadingAnovator && <p style={{ textAlign: "center", fontSize: 12 }}>Cargando datos...</p>}
          {errorAnovator && <p style={{ textAlign: "center", color: "red", fontSize: 12 }}>{errorAnovator}</p>}

          {!loadingAnovator && anovatorExams && (
            <div style={{ display: "grid", gap: 12 }}>
              {anovatorExams.map((exam) => (
                <div key={exam.id} style={{ border: "1px solid #eee", borderRadius: 12, padding: 14, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>

                  {/* Cabecera Tarjeta */}
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#444" }}>
                      {new Date(exam.gmtCreate).toLocaleDateString()}
                    </span>
                    <a
                      href={`https://www.anovator.com/report/index.html?id=${exam.id}&lang=es`}
                      target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, fontWeight: 800, color: "#D32F2F", textDecoration: "none", textTransform: "uppercase" }}
                    >
                      Ver Informe &rarr;
                    </a>
                  </div>

                  {/* Grid de Métricas con 5 Colores */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Metric5Ranges label="Peso" value={exam.weight} unit="kg" standard={exam.standard?.weightStandard} />
                    <Metric5Ranges label="BMI" value={exam.bmi} standard={exam.standard?.bmiStandard} />
                    <Metric5Ranges label="% Grasa" value={exam.fat} unit="%" standard={exam.standard?.fatStandard} />
                    <Metric5Ranges label="% Músculo" value={exam.muscle} unit="%" standard={exam.standard?.muscleStandard} />
                    <Metric5Ranges label="Agua" value={exam.water} unit="%" standard={exam.standard?.waterStandard} />
                    <Metric5Ranges label="Grasa Visceral" value={exam.inFat} standard={exam.standard?.visfatStandard} />
                    <Metric5Ranges label="Metabolismo" value={exam.bmr} unit="kcal" standard={exam.standard?.bmrStandard} />

                    {/* Proteína a veces viene como 'protein Standard' o 'proteinStandard' */}
                    <Metric5Ranges label="Proteína" value={exam.protein} unit="%"
                      standard={exam.standard?.proteinStandard || (exam.standard as any)?.["protein Standard"]}
                    />

                    <Metric5Ranges label="Masa Ósea" value={exam.bone} unit="kg" standard={exam.standard?.boneStandard} />

                    {/* Score General: Lógica manual porque no tiene standard de 5 rangos en API */}
                    <MetricScore label="Score Global" value={exam.score} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Componente para pintar una métrica usando la lógica de 5 rangos
function Metric5Ranges({ label, value, unit = "", standard }: { label: string; value?: number; unit?: string; standard?: AnovatorStandardRange }) {
  const status = get5RangeStatus(value, standard);
  const style = RANGE_STYLES[status];

  return (
    <div style={{
      background: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: 8,
      padding: "8px 10px",
      display: "flex", flexDirection: "column", justifyContent: "center"
    }}>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: style.text }}>
          {value ? Number(value).toFixed(1) : "—"}<span style={{ fontSize: 10, fontWeight: 600 }}>{unit}</span>
        </span>
        {status !== "neutral" && (
          <span style={{ fontSize: 9, fontWeight: 700, color: style.text, textTransform: "uppercase", opacity: 0.8 }}>
            {style.label}
          </span>
        )}
      </div>
    </div>
  );
}

// Componente especial para Score (0-100)
function MetricScore({ label, value }: { label: string; value?: number }) {
  // Lógica simple para Score: <70 (Bajo), 70-85 (Bueno), >85 (Excelente)
  let color = "#666";
  let bg = "#f5f5f5";
  let text = "—";

  if (value !== undefined) {
    if (value < 70) { color = "#D32F2F"; bg = "#FFEBEE"; text = "Mejorable"; }
    else if (value < 85) { color = "#F57C00"; bg = "#FFF3E0"; text = "Bueno"; }
    else { color = "#388E3C"; bg = "#E8F5E9"; text = "Excelente"; }
  }

  return (
    <div style={{
      background: bg, border: `1px solid ${color}40`,
      borderRadius: 8, padding: "8px 10px"
    }}>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: color }}>
          {value ?? "—"}
        </span>
        <span style={{ fontSize: 9, fontWeight: 700, color: color, textTransform: "uppercase" }}>
          {text}
        </span>
      </div>
    </div>
  );
}

/* === Subcomponente compacto === */
function Feature({
  icon,
  title,
  text,
}: {
  icon: string;
  title: string;
  text: string;
}) {
  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: "8px",
        background: "#fff",
        padding: "6px 8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        textAlign: "left",
      }}
    >
      <img
        src={icon}
        alt={title}
        style={{ width: 28, height: 28, marginBottom: 4, color: "#B71C1C" }}
      />
      <strong style={{ fontSize: "12px", display: "block" }}>{title}</strong>
      <p style={{ fontSize: "11px", margin: "2px 0 0", color: "#333" }}>{text}</p>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
          boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
        }}
      />
      <span>{label}</span>
    </span>
  );

}

function getScoreColor(score: number | null) {
  if (score === null) return "#888";
  if (score >= 85) return "#2e8b57"; // verde (85-100)
  if (score >= 70) return "#e8a317"; // ámbar (70-85)
  if (score >= 50) return "#c62828"; // rojo (50-70)
  return "#888"; // gris (<50)
}

function getScoreLabel(score: number | null) {
  if (score === null) return "Sin datos";
  if (score >= 85) return "Excelente";
  if (score >= 70) return "Bueno";
  return "En progreso";
}

function getAttendanceColor(weeksAttended?: number, windowWeeks = 52) {
  if (typeof weeksAttended !== "number" || !Number.isFinite(weeksAttended)) return "#888";
  const maxWeeks = Number.isFinite(windowWeeks) ? Math.max(1, windowWeeks) : 52;
  const weeks = Math.max(0, Math.min(weeksAttended, maxWeeks));
  if (weeks <= 10) return "#888";
  if (weeks <= 25) return "#c62828";
  if (weeks <= 39) return "#e8a317";
  return "#2e8b57";
}

function getFrequencyColor(avgPerWeek?: number) {
  if (typeof avgPerWeek !== "number" || !Number.isFinite(avgPerWeek)) return "#888";
  if (avgPerWeek <= 0) return "#888";
  if (avgPerWeek <= 0.5) return "#c62828";
  if (avgPerWeek <= 1.5) return "#e8a317";
  return "#2e8b57";
}

function getExtraServicesColor(extraServices?: number) {
  if (typeof extraServices !== "number" || !Number.isFinite(extraServices) || extraServices <= 0) {
    return "#888";
  }
  if (extraServices <= 2) return "#c62828";
  if (extraServices <= 4) return "#e8a317";
  return "#2e8b57";
}

function formatNumberValue(value?: number, fractionDigits = 1) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const formatted = value.toFixed(fractionDigits);
  return fractionDigits > 0 ? formatted.replace(/\\.0+$/, "") : formatted;
}

function BreakdownCard({
  title,
  value,
  extra,
  score,
  badgeColor,
}: {
  title: string;
  value: string;
  extra?: string;
  score?: number;
  badgeColor?: string;
}) {
  const appliedBadgeColor = badgeColor ?? getScoreColor(score ?? null);
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: "8px",
        padding: "8px 10px",
        background: "#fafafa",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ fontWeight: 700, fontSize: "12px", marginBottom: "4px" }}>
        {title}
      </div>
      <div style={{ fontSize: "12px", marginBottom: "2px" }}>{value}</div>
      {extra && <div style={{ fontSize: "11px", color: "#555" }}>{extra}</div>}
      {Number.isFinite(score) && (
        <div
          style={{
            marginTop: "6px",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: "11px",
            fontWeight: 700,
            color: appliedBadgeColor,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: appliedBadgeColor,
              display: "inline-block",
            }}
          />
          {score?.toFixed(1)} pts
        </div>
      )}
    </div>
  );
}

type ShareCardData = {
  score: number | null;
  label: string;
  breakdown: HumanScoreBreakdown | null;
  backgroundSrc?: string;
};

async function loadImageSafe(src?: string): Promise<HTMLImageElement | null> {
  if (!src) return null;
  return await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function buildShareCard(data: ShareCardData): Promise<Blob> {
  const { score, label, breakdown, backgroundSrc } = data;

  // Formato 9:16 para historias (sin franjas negras)
  const width = 1080;
  const height = 1920;

  const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
  const canvas = document.createElement("canvas");
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo crear el canvas para compartir");
  ctx.scale(dpr, dpr);

  // 1) FONDO: degradado claro (si no hay imagen de fondo)
  const background = await loadImageSafe(backgroundSrc);
  const logo = await loadImageSafe(logoSinFondo);
  const beMoreHuman = await loadImageSafe(beMoreHumanNoBg);
  if (background) {
    const scale = Math.max(
      width / background.naturalWidth,
      height / background.naturalHeight
    );
    const bgWidth = background.naturalWidth * scale;
    const bgHeight = background.naturalHeight * scale;
    const bgX = (width - bgWidth) / 2;
    const bgY = (height - bgHeight) / 2;
    ctx.drawImage(background, bgX, bgY, bgWidth, bgHeight);
  } else {
    // Si falla el fondo, al menos un degradado claro
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#ffffff");
    gradient.addColorStop(1, "#f5f5f5");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  // 2) Velo MUY suave para que el texto se lea, sin “manchas negras”
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  ctx.fillRect(0, 0, width, height);

  // 3) TARJETA BLANCA MÁS GRANDE
  const cardWidth = Math.min(width * 0.9, 950);   // antes 0.82
  const cardHeight = Math.min(height * 0.72, 1250); // antes 0.6
  const cardX = (width - cardWidth) / 2;
  const cardY = (height - cardHeight) / 2;

  roundRect(
    ctx,
    cardX,
    cardY,
    cardWidth,
    cardHeight,
    28,
    "rgba(255,255,255,0.95)",
    "rgba(255,255,255,0.4)"
  );

  // Logo de fondo sobre la tarjeta, detrás de todo el texto
  if (logo) {
    ctx.save();
    const maxLogoW = cardWidth * 0.7;
    const maxLogoH = cardHeight * 0.6;
    const scale = Math.min(maxLogoW / logo.naturalWidth, maxLogoH / logo.naturalHeight);
    const logoW = logo.naturalWidth * scale;
    const logoH = logo.naturalHeight * scale;
    const logoX = cardX + (cardWidth - logoW) / 2;
    const logoY = cardY + (cardHeight - logoH) / 2;
    ctx.globalAlpha = 0.08;
    ctx.drawImage(logo, logoX, logoY, logoW, logoH);
    ctx.restore();
  }

  const centerX = width / 2;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  // 4) TÍTULO
  ctx.fillStyle = "#b71c1c";
  ctx.font = "900 52px Arial";
  ctx.fillText("HUMAN SCORE", centerX, cardY + 84);
  ctx.fillStyle = "#333";
  ctx.font = "600 36px Arial";
  ctx.fillText("Compartido desde HUMAN", centerX, cardY + 140);

  // 5) CÍRCULO DE SCORE (más grande para el share)
  const ringOuter = 90; // radio externo (180px de diámetro)
  const ringThickness = 26;
  const radius = ringOuter - ringThickness / 2; // radio para el stroke
  const innerRadius = ringOuter - ringThickness; // radio del relleno interior
  const circleY = cardY + 250;
  ctx.lineWidth = ringThickness;
  ctx.strokeStyle = "#f0f0f0";
  ctx.beginPath();
  ctx.arc(centerX, circleY, radius, 0, Math.PI * 2);
  ctx.stroke();

  const pct = score ? Math.max(0, Math.min(score / 100, 1)) : 0;
  ctx.strokeStyle = getScoreColor(score);
  ctx.beginPath();
  ctx.arc(
    centerX,
    circleY,
    radius,
    -Math.PI / 2,
    -Math.PI / 2 + Math.PI * 2 * pct
  );
  ctx.stroke();
  // borde exterior negro
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#000";
  ctx.beginPath();
  ctx.arc(centerX, circleY, ringOuter, 0, Math.PI * 2);
  ctx.stroke();

  // Interior
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(centerX, circleY, innerRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = getScoreColor(score);
  ctx.font = "800 50px Arial";
  ctx.fillText(
    score !== null ? score.toFixed(1) : "—",
    centerX,
    circleY + 20
  );

  ctx.fillStyle = "#444";
  ctx.font = "700 36px Arial";
  ctx.fillText(label, centerX, circleY + 130);
  ctx.fillStyle = "#777";
  ctx.font = "600 28px Arial";
  ctx.fillText("sobre 100", centerX, circleY + 180);

  // 6) DETALLE
  const breakdownStart = circleY + ringOuter + 220;
  ctx.fillStyle = "#111";
  ctx.font = "800 42px Arial";
  ctx.fillText("Detalle", centerX, breakdownStart);

  ctx.fillStyle = "#555";
  ctx.font = "600 32px Arial";
  const lines: string[] = [
    `Asistencia: ${formatNumberValue(
      breakdown?.attendance?.weeksAttended,
      0
    )} / ${breakdown?.attendance?.windowWeeks ?? 52} semanas`,
    `Frecuencia: ${formatNumberValue(
      breakdown?.frequency?.avgPerWeek,
      1
    )} entrenos/sem`,
    `Servicios extra: ${formatNumberValue(
      breakdown?.extraServices?.count,
      0
    )} sesiones`,
  ];

  const spacing = 64;
  lines.forEach((text, idx) => {
    const y = breakdownStart + 56 + idx * spacing;
    ctx.fillText(text, centerX, y);
    const badge = scoreFromBreakdownIndex(breakdown, idx);
    const badgeColor = getBreakdownColorByIndex(breakdown, idx);
    if (badge !== null) {
      // un poco más pegado al borde de la tarjeta
      drawBadge(ctx, badge, centerX + cardWidth / 2 - 130, y, badgeColor);
    }
  });

  // 6.1) Logo adicional debajo del detalle (solo be-more-human-no-bg)
  if (beMoreHuman) {
    const maxW = cardWidth * 0.8;
    const maxH = 320;
    const scale = Math.min(maxW / beMoreHuman.naturalWidth, maxH / beMoreHuman.naturalHeight);
    const logoW = beMoreHuman.naturalWidth * scale;
    const logoH = beMoreHuman.naturalHeight * scale;
    const startY = breakdownStart + 50 + (lines.length - 1) * spacing + 120;
    const logoX = centerX - logoW / 2;
    ctx.drawImage(beMoreHuman, logoX, startY, logoW, logoH);
  }

  // 7) PIE
  ctx.fillStyle = "#b71c1c";
  ctx.font = "900 22px Arial";
  ctx.fillText("Generado en HUMAN", centerX, cardY + cardHeight - 46);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo generar la imagen para compartir"));
      },
      "image/png",
      0.95
    );
  });
}

function triggerImageDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

// Guarda la imagen en caché para que el plugin de Share (nativo) pueda adjuntarla.
async function persistImageForNativeShare(blob: Blob): Promise<string> {
  const base64 = await blobToBase64(blob);
  const cleanBase64 = base64.includes(",") ? base64.split(",")[1] : base64;
  const fileName = `human-score-${Date.now()}.png`;
  await Filesystem.writeFile({
    path: fileName,
    data: cleanBase64,
    directory: Directory.Cache,
  });
  const uri = await Filesystem.getUri({
    path: fileName,
    directory: Directory.Cache,
  });
  return uri.uri;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("No se pudo leer la imagen"));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}


function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
  stroke: string
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 2;
  ctx.fill();
  ctx.stroke();
}

function drawBadge(
  ctx: CanvasRenderingContext2D,
  score: number,
  x: number,
  y: number,
  badgeColor?: string
) {
  const color = badgeColor ?? getScoreColor(score);
  const value = `${score.toFixed(1)} pts`;
  const prevAlign = ctx.textAlign;
  const prevBaseline = ctx.textBaseline;
  const prevFont = ctx.font;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = prevFont; // mantener el mismo tamaño/peso que la línea de texto
  ctx.fillStyle = color;
  ctx.fillText(value, x, y);
  ctx.font = prevFont;
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBaseline;
}

function scoreFromBreakdownIndex(data: HumanScoreBreakdown | null, idx: number): number | null {
  if (!data) return null;
  if (idx === 0 && typeof data.attendance?.score === "number") return data.attendance.score;
  if (idx === 1 && typeof data.frequency?.score === "number") return data.frequency.score;
  if (idx === 2 && typeof data.extraServices?.score === "number") return data.extraServices.score;
  return null;
}

function getBreakdownColorByIndex(data: HumanScoreBreakdown | null, idx: number): string {
  if (idx === 0) return getAttendanceColor(data?.attendance?.weeksAttended, data?.attendance?.windowWeeks);
  if (idx === 1) return getFrequencyColor(data?.frequency?.avgPerWeek);
  if (idx === 2) return getExtraServicesColor(data?.extraServices?.count);
  return "#888";
}

// Función principal que determina cual de los 5 rangos aplicar
function get5RangeStatus(value?: number, std?: AnovatorStandardRange): RangeStatus {
  if (typeof value !== "number" || !Number.isFinite(value) || !std) return "neutral";

  // Extraemos los límites. Si falta alguno crítico, fallback a neutral o lógica parcial.
  const { fewest, fewer, more, most } = std;

  // Lógica de 5 pasos estricta
  if (fewest !== undefined && value < fewest) return "very_low";
  if (fewer !== undefined && value < fewer) return "low";
  if (more !== undefined && value <= more) return "normal";
  if (most !== undefined && value <= most) return "high";
  if (most !== undefined && value > most) return "very_high";

  // Fallback si faltan 'most' o 'fewest' pero hay 'more'/'fewer'
  if (more !== undefined && value > more) return "high";

  return "normal";
}
